import { useState, useEffect } from 'preact/hooks';
import { Route, Switch, Redirect, useLocation } from 'wouter';
import { useRegisterSW } from 'virtual:pwa-register/preact';
import { initialAppState, AppContext, appReducer } from './lib/state';
import { usePersistedReducer } from './lib/usePersistedReducer';
import { installTestApi } from './lib/test-api';
import { Brand } from './components/Brand';
import { Footer } from './components/Footer';
import { ChangelogModal } from './components/ChangelogModal';
import { Banner } from './components/Banner';
import { InstallPrompt } from './components/InstallPrompt';
import {
  isIosSafariNotInstalled,
  getInstallPromptDismissed,
  dismissInstallPrompt,
} from './lib/install-prompt';
import { checkForConstantsUpdate, applyConstantsUpdate } from './lib/constants-loader';
import { ALLOWED_CONSTANTS_HASHES } from './data/allowed-hashes';
import craConstants from './data/cra-2026.json';
const CURRENT_TAX_YEAR = String(craConstants.tax_year); // '2026'
import { openDb } from './lib/db';
import { Wizard } from './routes/Wizard';
import { Dashboard } from './routes/Dashboard';
import { History } from './routes/History';
import { Settings } from './routes/Settings';
import { About } from './routes/About';
import { Privacy } from './routes/Privacy';

installTestApi();

export function App() {
  const { state, dispatch, hydrated } = usePersistedReducer(appReducer, initialAppState);
  const [, setLocation] = useLocation();
  const [showChangelog, setShowChangelog] = useState(false);
  const [showInstallPrompt, setShowInstallPrompt] = useState(
    () => isIosSafariNotInstalled() && !getInstallPromptDismissed()
  );
  const [constantsUpdate, setConstantsUpdate] = useState<{ version: string; changelog: string } | null>(null);

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(r) {
      if (import.meta.env.DEV) console.debug('SW registered', r);
    },
    onRegisterError(error) {
      console.error('SW registration error', error);
    },
  });

  useEffect(() => {
    if (!hydrated) return;
    let cancelled = false;
    (async () => {
      const db = await openDb();
      if (cancelled) { db.close(); return; }
      const result = await checkForConstantsUpdate(db, state.constantsVersion, ALLOWED_CONSTANTS_HASHES);
      db.close();
      if (!cancelled && result.type === 'update-available') {
        setConstantsUpdate({ version: result.version, changelog: result.changelog });
      }
    })();
    return () => { cancelled = true; };
  }, [hydrated]);

  async function handleConstantsDismiss() {
    if (!constantsUpdate) return;
    const db = await openDb();
    await applyConstantsUpdate(db, CURRENT_TAX_YEAR, constantsUpdate.version);
    db.close();
    const verifiedAt = new Date().toISOString().split('T')[0];
    dispatch({ type: 'UPDATE_CONSTANTS', version: constantsUpdate.version, verifiedAt });
    setConstantsUpdate(null);
  }

  if (!hydrated) {
    return (
      <div class="min-h-screen bg-cream flex items-center justify-center">
        <p class="text-body/70 text-sm">Loading…</p>
      </div>
    );
  }

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      <div class="min-h-screen flex flex-col bg-cream text-body font-sans">
        <Brand
          constantsVersion={state.constantsVersion}
          onVersionClick={() => setShowChangelog(true)}
        />
        {constantsUpdate && (
          <Banner
            message={`TFSA constants updated to ${constantsUpdate.version} — your room calculation may have changed.`}
            onDismiss={handleConstantsDismiss}
            onAction={() => setShowChangelog(true)}
            actionLabel="What's new"
            variant="info"
          />
        )}
        {needRefresh && (
          <Banner
            message="Update available — improved version ready."
            onDismiss={() => updateServiceWorker(true)}
            actionLabel="Reload to update"
            onAction={() => updateServiceWorker(true)}
            variant="info"
          />
        )}
        <main class="flex-1">
          <Switch>
            <Route path="/setup">
              <Wizard onComplete={() => setLocation('/')} />
            </Route>
            <Route path="/log" component={History} />
            <Route path="/settings" component={Settings} />
            <Route path="/about" component={About} />
            <Route path="/privacy" component={Privacy} />
            <Route path="/">
              {state.wizardCompleted
                ? <Dashboard />
                : <Redirect to="/setup" />}
            </Route>
          </Switch>
        </main>
        <Footer
          constantsVersion={state.constantsVersion}
          constantsVerifiedAt={state.constantsVerifiedAt}
        />
        <ChangelogModal
          open={showChangelog}
          onClose={() => setShowChangelog(false)}
          version={state.constantsVersion}
          verifiedAt={state.constantsVerifiedAt}
        />
        {showInstallPrompt && (
          <InstallPrompt
            onDismiss={() => {
              dismissInstallPrompt();
              setShowInstallPrompt(false);
            }}
          />
        )}
      </div>
    </AppContext.Provider>
  );
}
